/**
 * 🔒 VAULT SEAL — DO NOT MODIFY (Feb 24 2026). See /app/memory/VAULT_SEAL.md.
 * Sealed until the user types "Upgrade Vault".
 */

import React from 'react';

/**
 * Tiny inline-SVG sparkline. No deps.
 * Props:
 *   values:    number[]
 *   width:     px
 *   height:    px
 *   stroke:    line color
 *   fill:      area color (rgba)
 */
const Sparkline = ({
  values = [],
  width = 100,
  height = 28,
  stroke = '#fbbf24',
  fill = 'rgba(251,191,36,0.18)',
  highlightIndex = -1,
  flameIndices = [],
}) => {
  if (!values.length) {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
        <line x1="0" y1={height - 1} x2={width} y2={height - 1} stroke={stroke} strokeOpacity="0.25" strokeDasharray="2 3" />
      </svg>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const pad = 2;
  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - pad - ((v - min) / range) * (height - pad * 2);
    return [x, y];
  });

  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const areaPath =
    `M0,${height} ` +
    points.map(([x, y]) => `L${x.toFixed(2)},${y.toFixed(2)}`).join(' ') +
    ` L${width},${height} Z`;

  const last = points[points.length - 1];
  const hi = highlightIndex >= 0 && highlightIndex < points.length ? points[highlightIndex] : null;
  // 5-pointed star path centered at (0,0), radius ~3.5
  const starPath = (cx, cy, r = 3.5) => {
    const pts = [];
    for (let i = 0; i < 10; i++) {
      const ang = (Math.PI / 5) * i - Math.PI / 2;
      const rr = i % 2 === 0 ? r : r * 0.45;
      pts.push([cx + rr * Math.cos(ang), cy + rr * Math.sin(ang)]);
    }
    return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ') + ' Z';
  };

  // Tiny flame glyph centered at (cx, cy) — simple stylised teardrop curve.
  const flamePath = (cx, cy, h = 6) => {
    const w = h * 0.55;
    return (
      `M${cx},${cy + h * 0.5} ` +
      `C${cx - w},${cy + h * 0.2} ${cx - w},${cy - h * 0.1} ${cx},${cy - h * 0.5} ` +
      `C${cx + w},${cy - h * 0.1} ${cx + w},${cy + h * 0.2} ${cx},${cy + h * 0.5} Z`
    );
  };

  const flameSet = new Set((flameIndices || []).filter((i) => i >= 0 && i < points.length));

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <path d={areaPath} fill={fill} />
      <path d={linePath} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.4" fill={stroke} />
      {[...flameSet].map((i) => {
        const [x, y] = points[i];
        return (
          <path
            key={`flame-${i}`}
            d={flamePath(x, y - 4)}
            fill="#fb923c"
            stroke="#fff7ed"
            strokeWidth="0.5"
          />
        );
      })}
      {hi && (
        <path d={starPath(hi[0], hi[1])} fill="#fde047" stroke="#fff" strokeWidth="0.5" />
      )}
    </svg>
  );
};

export default Sparkline;
